import React, { useCallback, useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { UserPlus, Edit3, Trash2, ShieldCheck, Users, Eye, X, Activity, Clock } from "lucide-react";
import { formatApiErrorDetail } from "@/context/AuthContext";

const ROLE_META = {
  admin:  { label: "Admin",  icon: ShieldCheck, color: "text-[#2563eb] bg-[#eff6ff] border-[#bfdbfe]" },
  staff:  { label: "Staff",  icon: Users,       color: "text-[#16a34a] bg-[#f0fdf4] border-[#bbf7d0]" },
  viewer: { label: "Viewer", icon: Eye,         color: "text-[#64748b] bg-[#f1f5f9] border-[#cbd5e1]" },
};

const ROLE_DESCRIPTION = {
  admin:  "Full access. Can manage team & permissions.",
  staff:  "Can manage orders, customers, packages. Cannot manage team.",
  viewer: "Read-only access to all admin pages.",
};

export default function AdminUsers() {
  const [users, setUsers] = useState([]);
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modal, setModal] = useState(null); // { mode: 'create' | 'edit', user?: obj }

  const load = useCallback(async () => {
    try {
      const [u, l] = await Promise.all([
        api.get("/admin/users"),
        api.get("/admin/logs?limit=100"),
      ]);
      setUsers(u.data || []);
      setLogs(l.data || []);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Failed to load team");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const onDelete = async (u) => {
    if (!window.confirm(`Deactivate ${u.email}? They'll no longer be able to sign in.`)) return;
    try {
      await api.delete(`/admin/users/${u.id}`);
      toast.success(`${u.email} deactivated`);
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Failed to deactivate user");
    }
  };

  return (
    <div className="max-w-7xl space-y-8" data-testid="admin-users-page">
      <header className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h1 className="font-display font-bold text-2xl text-[#0f1b2d]">Team & permissions</h1>
          <p className="mt-1 text-sm text-[#475569]">Manage admin, staff and viewer accounts. View an audit log of all actions taken.</p>
        </div>
        <button
          type="button"
          onClick={() => setModal({ mode: "create" })}
          data-testid="add-team-user-btn"
          className="inline-flex items-center gap-2 bg-[#2563eb] hover:bg-[#1d4ed8] text-white font-semibold text-sm px-4 py-2 rounded-md transition"
        >
          <UserPlus className="w-4 h-4" /> Add team member
        </button>
      </header>

      {/* Team table */}
      <section className="bg-white border border-[#e2e8f0] rounded-lg overflow-hidden" data-testid="team-users-section">
        <div className="px-5 py-3 border-b border-[#e2e8f0] text-xs font-semibold uppercase tracking-wider text-[#64748b]">
          Team members ({users.length})
        </div>
        {loading ? (
          <div className="p-8 text-center text-sm text-[#475569]">Loading…</div>
        ) : users.length === 0 ? (
          <div className="p-8 text-center text-sm text-[#475569]">No team members yet.</div>
        ) : (
          <table className="w-full text-sm" data-testid="team-users-table">
            <thead className="bg-[#f8fafc] text-xs uppercase tracking-wider text-[#64748b]">
              <tr>
                <th className="text-left px-4 py-3 font-semibold">Member</th>
                <th className="text-left px-4 py-3 font-semibold">Role</th>
                <th className="text-left px-4 py-3 font-semibold">Status</th>
                <th className="text-left px-4 py-3 font-semibold">Added</th>
                <th className="text-right px-4 py-3 font-semibold">Actions</th>
              </tr>
            </thead>
            <tbody>
              {users.map((u) => {
                const meta = ROLE_META[u.role] || ROLE_META.viewer;
                const Icon = meta.icon;
                return (
                  <tr key={u.id} className="border-t border-[#e2e8f0]" data-testid={`team-row-${u.id}`}>
                    <td className="px-4 py-3">
                      <div className="font-semibold text-[#0f1b2d]">{u.name || "—"}</div>
                      <div className="text-xs text-[#64748b]">{u.email}</div>
                    </td>
                    <td className="px-4 py-3">
                      <span className={`inline-flex items-center gap-1 text-xs font-semibold px-2 py-1 rounded-md border ${meta.color}`}>
                        <Icon className="w-3 h-3" /> {meta.label}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      {u.is_active ? (
                        <span className="text-xs font-semibold text-[#16a34a]">Active</span>
                      ) : (
                        <span className="text-xs font-semibold text-[#64748b]">Inactive</span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-xs text-[#475569]">{(u.created_at || "").slice(0, 10)}</td>
                    <td className="px-4 py-3 text-right">
                      <button
                        type="button"
                        onClick={() => setModal({ mode: "edit", user: u })}
                        data-testid={`edit-user-${u.id}`}
                        className="inline-flex items-center gap-1 text-xs font-semibold text-[#2563eb] hover:text-[#1d4ed8] px-2 py-1"
                      >
                        <Edit3 className="w-3 h-3" /> Edit
                      </button>
                      {u.role !== "admin" || users.filter((x) => x.role === "admin" && x.is_active).length > 1 ? (
                        <button
                          type="button"
                          onClick={() => onDelete(u)}
                          data-testid={`delete-user-${u.id}`}
                          className="inline-flex items-center gap-1 text-xs font-semibold text-red-600 hover:text-red-700 px-2 py-1 ml-1"
                        >
                          <Trash2 className="w-3 h-3" /> Deactivate
                        </button>
                      ) : null}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </section>

      {/* Activity log */}
      <section className="bg-white border border-[#e2e8f0] rounded-lg overflow-hidden" data-testid="activity-log-section">
        <div className="px-5 py-3 border-b border-[#e2e8f0] flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-[#64748b]">
          <Activity className="w-4 h-4 text-[#2563eb]" /> Activity log <span className="ml-auto normal-case text-[#94a3b8] font-normal">last 100 events</span>
        </div>
        {logs.length === 0 ? (
          <div className="p-8 text-center text-sm text-[#475569]">No activity yet.</div>
        ) : (
          <ul className="divide-y divide-[#e2e8f0]" data-testid="activity-log-list">
            {logs.map((l) => (
              <li key={l.id} className="px-5 py-3 flex items-start gap-3 text-sm" data-testid={`log-row-${l.id}`}>
                <div className="mt-1 w-2 h-2 rounded-full bg-[#2563eb] flex-shrink-0" />
                <div className="flex-1 min-w-0">
                  <div className="text-[#0f1b2d]">
                    <span className="font-semibold">{l.user_name}</span>
                    <span className="text-[#475569]"> ({l.user_role}) </span>
                    <span className="text-[#2563eb] font-semibold">{prettyAction(l.action)}</span>
                    {l.target && <span className="text-[#475569]"> on <span className="font-mono text-xs">{l.target}</span></span>}
                  </div>
                  {l.meta && Object.keys(l.meta).length > 0 && (
                    <div className="text-xs text-[#64748b] mt-0.5 font-mono truncate">{JSON.stringify(l.meta)}</div>
                  )}
                </div>
                <div className="text-xs text-[#94a3b8] flex items-center gap-1 flex-shrink-0">
                  <Clock className="w-3 h-3" /> {prettyTimestamp(l.created_at)}
                </div>
              </li>
            ))}
          </ul>
        )}
      </section>

      {modal && (
        <UserModal
          mode={modal.mode}
          user={modal.user}
          onClose={() => setModal(null)}
          onSaved={() => { setModal(null); load(); }}
        />
      )}
    </div>
  );
}

function prettyAction(a) {
  return String(a || "").replace(/_/g, " ");
}
function prettyTimestamp(s) {
  if (!s) return "";
  try {
    const d = new Date(s);
    const dd = String(d.getUTCDate()).padStart(2, "0");
    const mm = String(d.getUTCMonth() + 1).padStart(2, "0");
    const yyyy = d.getUTCFullYear();
    const hh = String(d.getUTCHours()).padStart(2, "0");
    const mi = String(d.getUTCMinutes()).padStart(2, "0");
    return `${dd}/${mm}/${yyyy} ${hh}:${mi}`;
  } catch { return s; }
}

function UserModal({ mode, user, onClose, onSaved }) {
  const isEdit = mode === "edit";
  const [form, setForm] = useState({
    name: user?.name || "",
    email: user?.email || "",
    password: "",
    role: user?.role || "staff",
    is_active: user?.is_active ?? true,
  });
  const [submitting, setSubmitting] = useState(false);

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!isEdit && form.password.length < 8) {
      toast.error("Password must be at least 8 characters"); return;
    }
    if (isEdit && form.password && form.password.length < 8) {
      toast.error("New password must be at least 8 characters"); return;
    }
    setSubmitting(true);
    try {
      if (isEdit) {
        const payload = { name: form.name, role: form.role, is_active: form.is_active };
        if (form.password) payload.password = form.password;
        await api.patch(`/admin/users/${user.id}`, payload);
        toast.success("Team member updated");
      } else {
        await api.post("/admin/users", {
          name: form.name, email: form.email, password: form.password, role: form.role,
        });
        toast.success("Team member added");
      }
      onSaved();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Failed to save");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 grid place-items-center bg-black/40 p-4" onClick={onClose}>
      <div onClick={(e) => e.stopPropagation()} className="bg-white rounded-lg max-w-md w-full shadow-xl" data-testid="user-modal">
        <div className="flex items-center justify-between p-4 border-b border-[#e2e8f0]">
          <h3 className="font-display font-bold text-lg text-[#0f1b2d]">{isEdit ? "Edit team member" : "Add team member"}</h3>
          <button onClick={onClose} className="text-[#475569] hover:text-[#0f1b2d]"><X className="w-4 h-4" /></button>
        </div>
        <form onSubmit={onSubmit} className="p-5 space-y-4">
          <Field label="Full name" required>
            <input required type="text" data-testid="modal-name" value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })} className="inp" />
          </Field>
          <Field label="Email" required>
            <input required type="email" disabled={isEdit} data-testid="modal-email" value={form.email}
              onChange={(e) => setForm({ ...form, email: e.target.value })} className="inp" />
          </Field>
          <Field label={isEdit ? "New password (optional)" : "Password"} required={!isEdit} hint="Minimum 8 characters">
            <input
              required={!isEdit}
              type="password"
              minLength={8}
              data-testid="modal-password"
              value={form.password}
              onChange={(e) => setForm({ ...form, password: e.target.value })}
              placeholder={isEdit ? "Leave blank to keep current" : ""}
              className="inp"
            />
          </Field>
          <Field label="Role" required>
            <select required data-testid="modal-role" value={form.role}
              onChange={(e) => setForm({ ...form, role: e.target.value })} className="inp">
              <option value="admin">Admin — full access</option>
              <option value="staff">Staff — orders, customers, packages</option>
              <option value="viewer">Viewer — read-only</option>
            </select>
            <p className="mt-1 text-xs text-[#64748b]">{ROLE_DESCRIPTION[form.role]}</p>
          </Field>
          {isEdit && (
            <label className="flex items-center gap-2 text-sm text-[#0f1b2d] cursor-pointer">
              <input type="checkbox" checked={form.is_active}
                onChange={(e) => setForm({ ...form, is_active: e.target.checked })}
                data-testid="modal-is-active" className="accent-[#2563eb] w-4 h-4" />
              Active (can sign in)
            </label>
          )}
          <div className="flex justify-end gap-2 pt-2">
            <button type="button" onClick={onClose}
              className="text-sm font-semibold text-[#475569] hover:text-[#0f1b2d] px-3 py-2">Cancel</button>
            <button type="submit" disabled={submitting} data-testid="modal-submit"
              className="bg-[#2563eb] hover:bg-[#1d4ed8] disabled:opacity-60 text-white font-semibold text-sm px-4 py-2 rounded-md">
              {submitting ? "Saving…" : (isEdit ? "Save changes" : "Add member")}
            </button>
          </div>
        </form>
      </div>
      <style>{`.inp { width: 100%; border: 1px solid #e2e8f0; background: #fff; color: #0f1b2d; border-radius: 6px; padding: 10px 12px; outline: none; font-size: 14px; }
        .inp:focus { border-color: #2563eb; box-shadow: 0 0 0 3px rgba(37,99,235,0.15); }
        .inp:disabled { background: #f1f5f9; color: #475569; }`}</style>
    </div>
  );
}

function Field({ label, required, hint, children }) {
  return (
    <label className="block">
      <span className="text-xs font-semibold text-[#0f1b2d]">
        {label} {required && <span className="text-red-500">*</span>}
        {hint && <span className="ml-2 text-[10px] text-[#64748b] font-normal">{hint}</span>}
      </span>
      <div className="mt-1.5">{children}</div>
    </label>
  );
}
